import { useState, useEffect, useRef } from 'react';
import { MeetingSession } from 'amazon-chime-sdk-js';
import { chimeService } from '@/services/chimeService';
import { metricsService } from '@/services/metricsService';
import { configService } from '@/services/configService';
import { ConnectionData } from '@/types/connect.types';
import { useAuth } from './useAuth';
import { isDebug, debug } from '@/config/constants';

export interface UseChimeSDKReturn {
  initializeSession: (connectionData: ConnectionData, sessionId: string, queueId?: string, featureName?: string, queueJoinTime?: number) => Promise<void>;
  endSession: (sessionId: string, callStartTime: number) => void;
  toggleMute: () => void;
  isMuted: boolean;
  isConnected: boolean;
  error: Error | null;
}

export function useChimeSDK(): UseChimeSDKReturn {
  const { getCredentials } = useAuth();
  const [isMuted, setIsMuted] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const sessionRef = useRef<MeetingSession | null>(null);
  const callStartTimeRef = useRef<number>(0);

  // Cleanup function to reset state
  const cleanup = () => {
    if (sessionRef.current) {
      chimeService.stopAudio(sessionRef.current);
      sessionRef.current = null;
    }
    setIsConnected(false);
    setIsMuted(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  const initializeSession = async (
    connectionData: ConnectionData,
    sessionId: string,
    queueId?: string,
    featureName?: string,
    queueJoinTime?: number
  ): Promise<void> => {
    try {
      setError(null);
      callStartTimeRef.current = Date.now();
      const t0 = performance.now();
      
      // Create Chime session
      const session = await chimeService.createSession(connectionData);
      sessionRef.current = session;
      const tSession = performance.now();

      // Set up audio session observers to handle disconnections
      const observer = {
        audioVideoDidStop: (sessionStatus: { statusCode: () => number }) => {
          const statusCode = sessionStatus.statusCode();
          
          // Log all disconnect reasons for debugging
          if (statusCode === 0) {
            debug.log('Call ended normally');
          } else if (statusCode === 1) {
            debug.log('Call ended by user');
          } else if (statusCode === 5) {
            debug.log('Call ended by contact flow');
          } else {
            // Unexpected error - show to user
            const errorMessage = `Call ended unexpectedly (code: ${statusCode})`;
            console.error(errorMessage);
            setError(new Error(errorMessage));
          }
          cleanup();
        },
        
        // Log connection failures
        audioVideoDidStopConnecting: (sessionStatus: { statusCode: () => number }) => {
          const statusCode = sessionStatus.statusCode();
          if (statusCode === 1) {
            debug.log('Call failed to connect (user canceled)');
          } else if (statusCode === 2) {
            debug.log('Call failed to connect (timeout)');
          } else if (statusCode === 3) {
            debug.log('Call failed to connect (internal error)');
          } else {
            debug.log(`Call failed to connect (status code: ${statusCode})`);
          }
          cleanup();
        },
        
        audioVideoDidStartConnecting: (reconnecting: boolean) => {
          // Silent - no log needed
          if (reconnecting) debug.log('⏳ Connecting to audio/video...');
        },
        audioVideoDidStart: async () => {
          const tConnected = performance.now();
          if (isDebug()) console.log(`⏱ Audio/video connected at ${Math.round(tConnected - t0)}ms after Chime init started`);
          debug.log('✓ Audio/video started');
          setIsConnected(true);
          
          // Record CALL_CONNECTED metric
          try {
            const credentials = await getCredentials();
            const waitTime = queueJoinTime ? Math.floor((Date.now() - queueJoinTime) / 1000) : 0;
            await metricsService.recordMetric(credentials, {
              metricType: 'CALL_CONNECTED',
              sessionId,
              queueId,
              featureName,
              waitTime,
            });
            
            // Record QUEUE_PROMOTED if this call came from the queue
            if (queueId && queueJoinTime) {
              await metricsService.recordMetric(credentials, {
                metricType: 'QUEUE_PROMOTED',
                sessionId,
                queueId,
                featureName,
                waitTime,
              });
            }
          } catch (err) {
            console.error('Failed to record CALL_CONNECTED metric:', err);
          }
        },
      };

      session.audioVideo.addObserver(observer);

      // Start audio with Voice Focus (enabled by default, configurable)
      const config = configService.getConfig();
      const enableVoiceFocus = config?.ENABLE_VOICE_FOCUS !== false; // Default to true
      await chimeService.startAudio(session, enableVoiceFocus);
      const tAudio = performance.now();
      setIsMuted(false);

      if (isDebug()) console.log(`⏱ Chime Init - Session: ${Math.round(tSession - t0)}ms, Audio: ${Math.round(tAudio - tSession)}ms, Total: ${Math.round(tAudio - t0)}ms`);
      debug.log('✓ Chime session initialized');
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to initialize Chime session');
      setError(error);
      cleanup();
      console.error('✗ Failed to initialize Chime session:', error);
      throw error;
    }
  };

  const endSession = async (sessionId: string, callStartTime: number): Promise<void> => {
    try {
      cleanup();
      setError(null);
      
      // Record CALL_ENDED metric
      try {
        const credentials = await getCredentials();
        const callDuration = Math.floor((Date.now() - callStartTime) / 1000);
        await metricsService.recordMetric(credentials, {
          metricType: 'CALL_ENDED',
          sessionId,
          callDuration,
        });
      } catch (err) {
        console.error('Failed to record CALL_ENDED metric:', err);
      }
      
      debug.log('✓ Chime session ended');
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to end Chime session');
      setError(error);
      console.error('✗ Failed to end Chime session:', error);
    }
  };

  const toggleMute = (): void => {
    try {
      if (!sessionRef.current) {
        throw new Error('No active session');
      }

      if (isMuted) {
        chimeService.unmuteAudio(sessionRef.current);
        setIsMuted(false);
      } else {
        chimeService.muteAudio(sessionRef.current);
        setIsMuted(true);
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to toggle mute');
      setError(error);
      console.error('✗ Failed to toggle mute:', error);
    }
  };

  return {
    initializeSession,
    endSession,
    toggleMute,
    isMuted,
    isConnected,
    error,
  };
}
